import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, errMsg, fmtDate } from "@/lib/api";
import { StatusChip } from "@/components/StatusChip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, Search } from "lucide-react";
import { toast } from "sonner";

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", create_login: false, password: "" });

  const [params] = useSearchParams();
  const fraudOnly = params.get("fraud") === "1";

  const load = () => {
    const params = {};
    if (search) params.search = search;
    if (status !== "all") params.status = status;
    if (fraudOnly) params.fraud = "1";
    api.get("/clients", { params }).then((r) => setClients(r.data)).catch(() => {});
  };
  useEffect(load, [search, status, fraudOnly]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/clients", form);
      toast.success("Client created");
      setOpen(false);
      setForm({ first_name: "", last_name: "", email: "", phone: "", create_login: false, password: "" });
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="space-y-5" data-testid="clients-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Clients</h1>
          <p className="text-sm text-slate-500">{clients.length} record{clients.length === 1 ? "" : "s"}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-client-btn" className="bg-[#0B2545] hover:bg-[#134074] text-white">
              <UserPlus className="h-4 w-4 mr-2" /> New Client
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">Add a client</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>First name</Label><Input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="new-client-first-name" className="mt-1" /></div>
                <div><Label>Last name</Label><Input required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="new-client-last-name" className="mt-1" /></div>
              </div>
              <div><Label>Email</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-client-email" className="mt-1" /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="new-client-phone" className="mt-1" /></div>
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <Checkbox checked={form.create_login} onCheckedChange={(v) => setForm({ ...form, create_login: !!v })} data-testid="new-client-create-login" />
                Create client portal login
              </label>
              {form.create_login && (
                <div><Label>Temporary password</Label><Input required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-client-password" className="mt-1" /></div>
              )}
              <Button type="submit" data-testid="new-client-submit" className="w-full bg-[#0B2545] hover:bg-[#134074] text-white">Create client</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-56">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="Search name or email…" value={search} onChange={(e) => setSearch(e.target.value)}
            data-testid="client-search-input" className="pl-9" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-44" data-testid="client-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="lead">Lead</SelectItem>
            <SelectItem value="onboarded">Onboarded</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
          </SelectContent>
        </Select>
        {fraudOnly && (
          <Link to="/admin/clients" className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full bg-rose-50 text-rose-700 border border-rose-200 px-3 py-1.5 hover:bg-rose-100" data-testid="fraud-filter-chip">
            Showing clients with fraud flags · clear ×
          </Link>
        )}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200">
              {["Client", "Status", "Items", "Open Disputes", "Assigned", "Added"].map((h) => (
                <th key={h} className="text-left py-2.5 px-3.5 text-xs font-mono uppercase tracking-[0.18em] text-slate-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {clients.map((c) => (
              <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors duration-150">
                <td className="py-2.5 px-3.5">
                  <Link to={`/admin/clients/${c.id}`} className="font-medium text-blue-700 hover:underline" data-testid={`client-row-${c.id.slice(0, 8)}`}>{c.name}</Link>
                  <div className="text-xs text-slate-400">{c.email}</div>
                  {c.fraud_flags > 0 && (
                    <span className={`mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide border ${c.has_ftc_report ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}`} data-testid={`fraud-badge-${c.id.slice(0, 8)}`}>
                      {c.fraud_flags} fraud flag{c.fraud_flags === 1 ? "" : "s"} · {c.has_ftc_report ? "FTC report on file" : "awaiting FTC report"}
                    </span>
                  )}
                </td>
                <td className="py-2.5 px-3.5"><StatusChip status={c.status} /></td>
                <td className="py-2.5 px-3.5 text-slate-600">{c.items_count}</td>
                <td className="py-2.5 px-3.5 text-slate-600">{c.open_disputes}</td>
                <td className="py-2.5 px-3.5 text-slate-600">{c.assigned_to || "—"}</td>
                <td className="py-2.5 px-3.5 text-slate-500">{fmtDate(c.created_at)}</td>
              </tr>
            ))}
            {clients.length === 0 && (
              <tr><td colSpan={6} className="py-10 text-center text-slate-400">No clients match your filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
